import { findRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { type NotifyInput, notify, userName, wmsRoute } from './notification.shared'
import { almAutoApprove, clmAutoApprove } from './slaSetting'

/**
 * Aturan notifikasi untuk perpindahan status workflow (`setSubmission`): satu tabel `workflow:aksi → notifikasi`.
 * Dipanggil engine sesudah transisi berhasil; aturan yang butuh data di luar baris entitas (mis. operator tugas yang
 * dibuat handler) dipasang langsung di handler masing-masing dengan `notify()`.
 *
 * Kontrak keselamatan: `notifyAfterTransition` membungkus semuanya dalam try/catch — galat aturan tidak pernah
 * menggagalkan transisi.
 */

export type TransitionContext = {
  workflowCode: string
  actionName: string
  entity: string
  entityId: string
  reason: string
  /** Baris entitas setelah transisi. */
  row: LocalRow
  /** Aksi sistem tanpa aktor manusia (sweep SLA / auto-complete): penerima tidak mengecualikan user aktif; dedupe otomatis. */
  system?: boolean
}

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const DOC_NO_FIELDS = [
  'pr_no',
  'ro_no',
  'req_no',
  'transfer_no',
  'trf_no',
  'claim_no',
  'dsp_no',
  'bap_no',
  'opn_no',
  'dsc_no',
  'receipt_no',
  'return_no',
  'assignment_no',
  'batch_no'
]

/** Nomor dokumen yang mudah dibaca (mis. `PR-260818-001`); jatuh ke kolom `*_no` pertama, lalu id. */
export function docNoOf(row: LocalRow): string {
  for (const field of DOC_NO_FIELDS) if (typeof row[field] === 'string' && row[field]) return String(row[field])
  const generic = Object.keys(row).find(key => /_no$/.test(key) && typeof row[key] === 'string' && row[key])
  return generic ? String(row[generic]) : String(row.id).slice(0, 8)
}

const creatorOf = (row: LocalRow) => idOf(row.user_created) || null
const withReason = (text: string, reason: string) => (reason ? `${text} Alasan: ${reason}` : text)

/** Gudang (RM / FG) suatu baris yang punya `warehouse_id`. */
const warehouseKindOf = (row: LocalRow): 'RM' | 'FG' =>
  findRow('mt_warehouses', idOf(row.warehouse_id))?.kind === 'FG' ? 'FG' : 'RM'

type Rule = (context: TransitionContext) => NotifyInput | NotifyInput[] | null

const ref = (context: TransitionContext) => ({ type: context.entity, id: context.entityId })

const RULES: Record<string, Rule> = {
  /* ---------------------------------------------------------------- PRC — Purchase Request */
  ...Object.fromEntries(
    ['ACT_PRC_SUBMIT', 'ACT_PRC_RESUBMIT_SPV', 'ACT_PRC_RESUBMIT_PURCH'].map(action => [
      `WF-PRC-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { roles: ['wh_rm_supervisor'] },
        type: 'PR_SUBMITTED',
        title: `PR ${docNoOf(context.row)} menunggu approval Supervisor`,
        body: `PR ${docNoOf(context.row)} diajukan oleh ${userName(creatorOf(context.row))} — buka Daftar Purchase Request.`,
        link: wmsRoute('procurement/daftar-pr'),
        ref: ref(context),
        dedupeKey: `PR_SUBMITTED:${context.entityId}`
      })
    ])
  ),
  'WF-PRC-01:ACT_PRC_APP_SPV': context => ({
    to: { roles: ['purchasing'] },
    type: 'PR_PENDING_PURCHASING',
    title: `PR ${docNoOf(context.row)} menunggu approval Purchasing`,
    body: `PR ${docNoOf(context.row)} sudah disetujui Supervisor RM — pilih supplier dan setujui di Approval PR.`,
    link: wmsRoute('procurement/approval-pr'),
    ref: ref(context)
  }),
  'WF-PRC-01:ACT_PRC_APP_PURCH': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'PR_APPROVED',
    title: `PR ${docNoOf(context.row)} disetujui`,
    body: `PR ${docNoOf(context.row)} disetujui penuh oleh Purchasing.`,
    link: wmsRoute('procurement/daftar-pr'),
    ref: ref(context),
    severity: 'success'
  }),
  'WF-PRC-01:ACT_PRC_REJ_SPV': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'PR_REJECTED',
    title: `PR ${docNoOf(context.row)} ditolak Supervisor`,
    body: withReason(`PR ${docNoOf(context.row)} ditolak Supervisor RM.`, context.reason),
    link: wmsRoute('procurement/daftar-pr'),
    ref: ref(context),
    severity: 'warning'
  }),
  'WF-PRC-01:ACT_PRC_REJ_PURCH': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'PR_REJECTED',
    title: `PR ${docNoOf(context.row)} ditolak Purchasing`,
    body: withReason(`PR ${docNoOf(context.row)} ditolak Purchasing.`, context.reason),
    link: wmsRoute('procurement/daftar-pr'),
    ref: ref(context),
    severity: 'warning'
  }),

  /* ---------------------------------------------------------------- TOB-01 — Penerimaan tembakau */
  'WF-TOB-01:ACT_TOB_REC_SEND': context => ({
    to: { roles: ['wh_rm_supervisor'] },
    type: 'TOBACCO_RECEIPT_PENDING',
    title: `Penerimaan tembakau ${docNoOf(context.row)} menunggu approval`,
    body: `Penerimaan tembakau ${docNoOf(context.row)} menunggu persetujuan Supervisor RM.`,
    link: wmsRoute('procurement/inbound-receiving'),
    ref: ref(context)
  }),
  'WF-TOB-01:ACT_TOB_REC_APP': context => ({
    to: { roles: ['admin_wh_rm'], userIds: [creatorOf(context.row)] },
    type: 'TOBACCO_RECEIPT_STATUS',
    title: `Penerimaan tembakau ${docNoOf(context.row)} disetujui`,
    body: `Penerimaan tembakau ${docNoOf(context.row)} disetujui — terbitkan stiker dan tugaskan putaway.`,
    link: wmsRoute('procurement/inbound-receiving'),
    ref: ref(context),
    severity: 'success'
  }),
  'WF-TOB-01:ACT_TOB_REC_REJ': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'TOBACCO_RECEIPT_STATUS',
    title: `Penerimaan tembakau ${docNoOf(context.row)} ditolak`,
    body: withReason(`Penerimaan tembakau ${docNoOf(context.row)} ditolak Supervisor RM.`, context.reason),
    link: wmsRoute('procurement/inbound-receiving'),
    ref: ref(context),
    severity: 'warning'
  }),

  /* ---------------------------------------------------------------- REQ — Request material */
  /* Kirim biasa dan kirim dengan kekurangan stok (`ACT_REQ_SHORTAGE_PR`) → penerima & jenis yang sama. */
  ...Object.fromEntries(
    ['ACT_REQ_SEND', 'ACT_REQ_SHORTAGE_PR'].map(action => [
      `WF-REQ-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { roles: ['admin_wh_rm'] },
        type: 'REQUEST_SUBMITTED',
        title: `Request material ${docNoOf(context.row)} menunggu penugasan`,
        body: `${userName(creatorOf(context.row))} mengirim request material ${docNoOf(context.row)}${
          action === 'ACT_REQ_SHORTAGE_PR' ? ' (ada kekurangan stok — PR dibuat untuk selisihnya)' : ''
        } — buka Request Material.`,
        link: wmsRoute('request-production/request-material'),
        ref: ref(context)
      })
    ])
  ),
  'WF-REQ-01:ACT_REQ_ASSIGN': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'REQUEST_STATUS',
    title: `Request ${docNoOf(context.row)}: tugas picking diterbitkan`,
    body: `Gudang sudah menerbitkan tugas picking untuk request ${docNoOf(context.row)}.`,
    link: wmsRoute('request-production/request-material'),
    ref: ref(context)
  }),
  'WF-REQ-01:ACT_REQ_PICK_DONE': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'REQUEST_STATUS',
    title: `Request ${docNoOf(context.row)} siap diserahterimakan`,
    body: `Picking request ${docNoOf(context.row)} selesai — konfirmasi di Serah Terima ke Produksi.`,
    link: wmsRoute('request-production/serah-terima-produksi'),
    ref: ref(context),
    severity: 'success'
  }),

  /* ---------------------------------------------------------------- RTN — Return Order */
  ...Object.fromEntries(
    ['ACT_RTN_SUBMIT', 'ACT_RTN_REVISE'].map(action => [
      `WF-RTN-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { roles: ['wh_rm_supervisor'] },
        type: 'RO_PENDING_APPROVAL',
        title: `RO ${docNoOf(context.row)} menunggu approval Supervisor`,
        body: `RO ${docNoOf(context.row)} diajukan — perlu keputusan Supervisor RM.`,
        link: wmsRoute('return-order-rm/daftar-ro'),
        ref: ref(context)
      })
    ])
  ),
  'WF-RTN-01:ACT_RTN_APP_SPV': context => ({
    to: { roles: ['kadiv_wh_rm'] },
    type: 'RO_PENDING_APPROVAL',
    title: `RO ${docNoOf(context.row)} menunggu approval Kadiv`,
    body: `RO ${docNoOf(context.row)} disetujui Supervisor — perlu keputusan Kadiv WH RM.`,
    link: wmsRoute('return-order-rm/daftar-ro'),
    ref: ref(context)
  }),
  'WF-RTN-01:ACT_RTN_APP_KADIV': context => ({
    to: { roles: ['purchasing'] },
    type: 'RO_PENDING_APPROVAL',
    title: `RO ${docNoOf(context.row)} menunggu approval & jadwal retur`,
    body: `RO ${docNoOf(context.row)} disetujui Kadiv — tetapkan rencana tanggal retur.`,
    link: wmsRoute('return-order-rm/daftar-ro'),
    ref: ref(context)
  }),
  'WF-RTN-01:ACT_RTN_APP_PURCH': context => ({
    to: { roles: ['admin_wh_rm'], userIds: [creatorOf(context.row)] },
    type: 'RO_STATUS',
    title: `RO ${docNoOf(context.row)} disetujui Purchasing`,
    body: `RO ${docNoOf(context.row)} disetujui — tugaskan operator pengambilan.`,
    link: wmsRoute('return-order-rm/daftar-ro'),
    ref: ref(context),
    severity: 'success'
  }),
  ...Object.fromEntries(
    ['ACT_RTN_REJ_SPV', 'ACT_RTN_REJ_KADIV', 'ACT_RTN_REJ_PURCH'].map(action => [
      `WF-RTN-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { userIds: [creatorOf(context.row)] },
        type: 'RO_STATUS',
        title: `RO ${docNoOf(context.row)} ditolak`,
        body: withReason(`RO ${docNoOf(context.row)} ditolak.`, context.reason),
        link: wmsRoute('return-order-rm/daftar-ro'),
        ref: ref(context),
        severity: 'warning'
      })
    ])
  ),

  /* ---------------------------------------------------------------- TRF — Transfer antar gudang */
  'WF-TRF-01:ACT_TRF_SEND': context => ({
    to: { roles: ['admin_wh_rm'] },
    type: 'TRANSFER_PENDING_APPROVAL',
    title: `Transfer ${docNoOf(context.row)} menunggu review Admin`,
    body: `Transfer ${docNoOf(context.row)} diajukan Admin Sansugi — perlu review Admin WH RM.`,
    link: wmsRoute('wip-transfer/kirim-antar-gudang'),
    ref: ref(context)
  }),
  'WF-TRF-01:ACT_TRF_ADMIN_ACCEPT': context => ({
    to: { roles: ['wh_rm_supervisor'] },
    type: 'TRANSFER_PENDING_APPROVAL',
    title: `Transfer ${docNoOf(context.row)} menunggu approval Surat Jalan Internal`,
    body: `Transfer ${docNoOf(context.row)} menunggu approval Supervisor RM.`,
    link: wmsRoute('wip-transfer/kirim-antar-gudang'),
    ref: ref(context)
  }),
  'WF-TRF-01:ACT_TRF_APP_SPV': context => ({
    to: { roles: ['kadiv_wh_rm'] },
    type: 'TRANSFER_PENDING_APPROVAL',
    title: `Transfer ${docNoOf(context.row)} menunggu approval Kadiv`,
    body: `Transfer ${docNoOf(context.row)} disetujui Supervisor — perlu keputusan Kadiv WH RM.`,
    link: wmsRoute('wip-transfer/kirim-antar-gudang'),
    ref: ref(context)
  }),
  'WF-TRF-01:ACT_TRF_APP_KADIV': context => ({
    to: { roles: ['admin_wh_rm'], userIds: [creatorOf(context.row)] },
    type: 'TRANSFER_STATUS',
    title: `Transfer ${docNoOf(context.row)} disetujui`,
    body: `Transfer ${docNoOf(context.row)} disetujui penuh — tugaskan operator picking.`,
    link: wmsRoute('wip-transfer/kirim-antar-gudang'),
    ref: ref(context),
    severity: 'success'
  }),
  ...Object.fromEntries(
    ['ACT_TRF_ADMIN_REJ', 'ACT_TRF_REJ_SPV', 'ACT_TRF_REJ_KADIV'].map(action => [
      `WF-TRF-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { userIds: [creatorOf(context.row)] },
        type: 'TRANSFER_STATUS',
        title: `Transfer ${docNoOf(context.row)} ditolak`,
        body: withReason(`Transfer ${docNoOf(context.row)} ditolak.`, context.reason),
        link: wmsRoute('wip-transfer/kirim-antar-gudang'),
        ref: ref(context),
        severity: 'warning'
      })
    ])
  ),
  'WF-TRF-01:ACT_TRF_DISPATCH': context => ({
    to: { userIds: [creatorOf(context.row)] },
    type: 'TRANSFER_STATUS',
    title: `Transfer ${docNoOf(context.row)} sudah berangkat`,
    body: `Transfer ${docNoOf(context.row)} sudah berangkat — tekan Terima Barang setelah barang tiba.`,
    link: wmsRoute('wip-transfer/kirim-antar-gudang'),
    ref: ref(context)
  }),

  /* ---------------------------------------------------------------- DSR — Selisih opname */
  ...Object.fromEntries(
    ['ACT_DSR_ESCALATE_WO', 'ACT_DSR_AUTO_ESCALATE'].map(action => [
      `WF-DSR-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { roles: [warehouseKindOf(context.row) === 'FG' ? 'kadiv_wh_fg' : 'kadiv_wh_rm'] },
        type: 'DISCREPANCY_ESCALATED',
        title: `Selisih ${docNoOf(context.row)} menunggu approval write-off`,
        body: `Selisih ${docNoOf(context.row)} dieskalasi untuk write-off — perlu keputusan Kadiv.`,
        link: wmsRoute('stock-opname/selisih'),
        ref: ref(context),
        severity: 'critical'
      })
    ])
  ),
  ...Object.fromEntries(
    ['ACT_DSR_APP_WRITEOFF', 'ACT_DSR_REJ_WRITEOFF'].map(action => [
      `WF-DSR-01:${action}`,
      (context: TransitionContext): NotifyInput => ({
        to: { roles: [warehouseKindOf(context.row) === 'FG' ? 'admin_wh_fg' : 'admin_wh_rm'] },
        type: 'DISCREPANCY_DECIDED',
        title: `Write-off ${docNoOf(context.row)} ${action === 'ACT_DSR_APP_WRITEOFF' ? 'disetujui' : 'ditolak'}`,
        body: withReason(
          `Kadiv ${action === 'ACT_DSR_APP_WRITEOFF' ? 'menyetujui' : 'menolak'} write-off ${docNoOf(context.row)}.`,
          context.reason
        ),
        link: wmsRoute('stock-opname/selisih'),
        ref: ref(context),
        severity: action === 'ACT_DSR_APP_WRITEOFF' ? 'success' : 'warning'
      })
    ])
  ),

  /* ---------------------------------------------------------------- CLM — Claim customer (v7.50) */
  /* Tugas sortir / putaway dan sortir ulang dikirim handler `claimFg` dengan `notify()`; auto-approve (aksi sistem) lewat aturan di bawah. */
  'WF-CLM-01:ACT_CLM_FINISH_SORT': context => {
    const rule = clmAutoApprove()
    return {
      to: { roles: ['supervisor_wh_fg'] },
      type: 'CLAIM_AWAITING_APPROVAL',
      title: `Hasil sortir claim ${docNoOf(context.row)} menunggu keputusan Anda`,
      body: rule.enabled
        ? `Setujui Hasil Sortir atau Minta Sortir Ulang — disetujui otomatis dalam ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'menit'} bila belum diputuskan.`
        : 'Setujui Hasil Sortir atau Minta Sortir Ulang — aturan auto-approve sedang dimatikan.',
      link: wmsRoute(`customer-claim/detail-claim/${context.entityId}`),
      ref: ref(context),
      dedupeKey: `CLAIM_AWAITING_APPROVAL:${context.entityId}:${String(context.row.sort_locked_at ?? '')}`
    }
  },

  'WF-CLM-01:ACT_CLM_AUTO_APPROVE': context => ({
    to: { roles: ['admin_wh_fg', 'supervisor_wh_fg'] },
    type: 'CLAIM_AUTO_APPROVED',
    title: `Claim ${docNoOf(context.row)} disetujui otomatis`,
    body: `Hasil sortir disetujui sistem karena belum diputuskan Supervisor — Admin dapat menekan Tugaskan Putaway.`,
    link: wmsRoute(`customer-claim/detail-claim/${context.entityId}`),
    ref: ref(context),
    severity: 'warning',
    dedupeKey: `CLAIM_AUTO_APPROVED:${context.entityId}`
  }),

  /* ---------------------------------------------------------------- GAB — Barang Lepas / Gabung (v7.50 A-01) */
  'WF-GAB-01:ACT_GAB_AUTO_APPROVE': context => {
    const rule = almAutoApprove()
    const sku = findRow('mt_product', idOf(context.row.sku_id))
    return {
      to: { roles: ['supervisor_wh_fg'] },
      type: 'GAB_AUTO_APPROVED',
      title: `Permintaan ${String(context.row.png_no)} disetujui otomatis`,
      body: `Permintaan ${String(context.row.jenis)} ${String(sku?.code ?? '')} dari ${String(context.row.source_text)} disetujui sistem karena Admin WH FG belum memutuskan dalam ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'menit'}.`,
      link: wmsRoute('merge-consolidation-fg/barang-lepas-gabung'),
      ref: ref(context),
      severity: 'warning',
      dedupeKey: `GAB_AUTO_APPROVED:${context.entityId}`
    }
  },

  /* ---------------------------------------------------------------- FG — Penerimaan FG (ALM) */
  'WF-FG-01:ACT_FG_FEED_RECV': context => ({
    to: { roles: ['admin_wh_fg'] },
    type: 'ALM_REQUEST_CREATED',
    title: `Batch ${docNoOf(context.row)} menunggu Terima & Cek`,
    body: `Batch FG ${docNoOf(context.row)} dari labeler menunggu Terima & Cek Admin WH FG.`,
    link: wmsRoute('inbound-fg/penerimaan-fg'),
    ref: ref(context)
  }),
  'WF-FG-01:ACT_FG_SUBMIT': context => ({
    to: { roles: ['supervisor_wh_fg'] },
    type: 'ALM_PENDING_APPROVAL',
    title: `Batch ${docNoOf(context.row)} menunggu approval Supervisor`,
    body: `Batch FG ${docNoOf(context.row)} sudah dicek — perlu approval Supervisor WH FG.`,
    link: wmsRoute('inbound-fg/terima-cek-fg'),
    ref: ref(context)
  }),
  'WF-FG-01:ACT_FG_AUTO_APP': context => ({
    to: { roles: ['admin_wh_fg', 'supervisor_wh_fg'] },
    type: 'ALM_AUTO_APPROVED',
    title: `Batch ${docNoOf(context.row)} disetujui otomatis`,
    body: `Batch FG ${docNoOf(context.row)} disetujui sistem karena melewati batas waktu approval.`,
    link: wmsRoute('inbound-fg/penerimaan-fg'),
    ref: ref(context),
    severity: 'warning',
    dedupeKey: `ALM_AUTO_APPROVED:${context.entityId}`
  }),

  /* ---------------------------------------------------------------- DSP — Pemusnahan */
  'WF-DSP-01:ACT_DSP_FG_SEND': context => ({
    to: { roles: ['supervisor_wh_fg'] },
    type: 'DESTRUCTION_PENDING_APPROVAL',
    title: `Pemusnahan FG ${docNoOf(context.row)} menunggu approval`,
    body: `Dokumen pemusnahan FG ${docNoOf(context.row)} menunggu persetujuan Supervisor FG.`,
    link: wmsRoute('destruction-fg/dokumen-pemusnahan'),
    ref: ref(context)
  }),
  'WF-DSP-02:ACT_DSP_RM_SEND': context => ({
    to: { roles: ['wh_rm_supervisor'] },
    type: 'DESTRUCTION_PENDING_APPROVAL',
    title: `Jadwal pemusnahan RM ${docNoOf(context.row)} menunggu approval`,
    body: `Jadwal pemusnahan RM ${docNoOf(context.row)} menunggu persetujuan Supervisor RM.`,
    link: wmsRoute('destruction-rm/berita-acara-pemusnahan'),
    ref: ref(context)
  })
}

/**
 * Dipanggil `setSubmission` (aksi manusia) dan `runSystemAction` (aksi sistem) sesudah transisi sukses. Tidak pernah
 * melempar. Aksi sistem: user aktif tidak dikecualikan dan selalu ada `dedupeKey` (sweep berulang tidak menggandakan).
 */
export function notifyAfterTransition(context: TransitionContext): void {
  try {
    const rule = RULES[`${context.workflowCode}:${context.actionName}`]
    if (!rule) return
    const result = rule(context)
    for (const input of Array.isArray(result) ? result : result ? [result] : []) {
      notify(
        context.system
          ? {
              ...input,
              includeActor: true,
              dedupeKey: input.dedupeKey ?? `${input.type}:${context.actionName}:${context.entityId}`
            }
          : input
      )
    }
  } catch (error) {
    console.warn('[local-db] aturan notifikasi gagal (diabaikan):', error)
  }
}

/** Kunci aturan yang terdaftar (`WF-…:ACT_…`) — dipakai uji dan dokumentasi. */
export const NOTIFICATION_RULE_KEYS = Object.keys(RULES)
